import React, {useState} from "react";
import NoIndexDialog from "../../noIndex/NoIndexDialog";
import {
    DialogTitle,
    Box,
    Stepper,
    Step,
    StepButton,
    IconButton,
    makeStyles,
    Typography,
} from "@material-ui/core";
import {
    useBreakpoints, 
    useMarketplaceTutorial
} from "../../../../hooks";
import StreamlineIcon from "../../../widget/streamline-icon/StreamlineIcon";
import ArrowLeft1 from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/arrows-diagrams/arrows/arrow-left-1.svg";
import CloseIcon from "../../../widget/streamline-icon/CloseIcon";
import TutorialFooter from "./TutorialFooter";
import TutorialElements from "./TutorialElements";
import CustomButton from "../../../widget/button/CustomButton";
import {useI18N} from "../../../../common/common";
import PropTypes from "prop-types";
import {useMarketplaceShareInfo} from "../../../../store/providers/MarketplaceShareInfoProvider";

const useStyles = makeStyles((theme) => ({
    root: {
        [theme.breakpoints.up("sm")]: {
            width: theme.spacing(60),
        },
    },
    img: {
        width: theme.spacing(12.5),
        height: theme.spacing(12.5),
    },
    imgPos: {
        display: "flex",
        justifyContent: "center"
    },
    content: {
        padding: theme.spacing(3, 3),
    },
    stepper: {
        "&.MuiStepper-root": {
            padding: 0,
            marginTop: theme.spacing(4.4),
            marginBottom: theme.spacing(5),
            marginLeft: "auto",
            marginRight: "auto",
            width: theme.spacing(42.5)
        }
    },
    step: {
        "& .MuiStepIcon-root": {
            color: theme.palette.grey[500],
            width: theme.spacing(4),
            height: theme.spacing(4),
            "&.MuiStepIcon-active": {
                color: theme.palette.green,
            }
        },
        "& .MuiStepConnector-line": {
            borderColor: theme.palette.secondary.main
        },
        "& .MuiStepLabel-label": {
            color: theme.palette.text.primary,
            fontSize: theme.spacing(1.5),
            letterSpacing: theme.spacing(0.025),
            marginTop: theme.spacing(1),
            width: theme.spacing(10),
            "&.MuiStepLabel-active": {
                color: theme.palette.text.primary,
                fontWeight: "normal"
            }
        },
        "& .MuiStepConnector-alternativeLabel": {
            top: theme.spacing(2)
        },
        "& .MuiStepIcon-text": {
            fontWeight: 600,
            fill: theme.palette.text.white,
        }
    },
    contentButton: {
        marginBottom: theme.spacing(5),
        textAlign: "center"
    },
    backButton: {
        position: "absolute",
        marginTop: "-6px",
        marginLeft: theme.spacing(1),
        left: 0
    },
    backIcon: {
        width: theme.spacing(3),
        height: theme.spacing(3)
    },
    closeButton: {
        position: "absolute",
        marginTop: "-6px",
        right: theme.spacing(1)
    },
    mb5: {
        marginBottom: theme.spacing(5)
    },
    title: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(5),
        textAlign: "center"
    }
}));

const TutorialStepperDialog = ({open, onCloseTip}) => {
    const classes = useStyles();
    const [activeStep, setActiveStep] = useState(0);
    const {items} = useMarketplaceTutorial();
    const {shareData} = useMarketplaceShareInfo();
    const {isExtraSmallScreen} = useBreakpoints();
    const {i18n} = useI18N();

    const handleGoToPreviousStep = () => {
        setActiveStep(activeStep - 1);
    };

    const handleChangeStep = (step) => {
        setActiveStep(step);
    };

    const handleClose = () => {
        onCloseTip(false);
        setActiveStep(0);
    };

    return (
        <NoIndexDialog
            open={open}
            onBackdropClick={handleClose}
            fullScreen={isExtraSmallScreen()}
        >
            <Box
                display={"flex"}
                alignItems={"center"}
                flexDirection={"column"}
                className={classes.root}
            >
                <DialogTitle>
                    {activeStep > 0 &&
                        <CustomButton
                            disableElevation
                            color={"black"}
                            size={"small"}
                            startIcon={
                                <StreamlineIcon className={classes.backIcon}><ArrowLeft1 /></StreamlineIcon>
                            }
                            onClick={handleGoToPreviousStep}
                            className={classes.backButton}
                        >
                            {i18n("dialog.marketplace_onboarding.back")}
                        </CustomButton>
                    }
                    <IconButton
                        aria-label="close"
                        className={classes.closeButton}
                        onClick={handleClose}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                {items && items.length > 0 &&
                    <div className={classes.content}>
                        <Box
                            className={classes.imgPos}
                        >
                            <img
                                className={classes.img}
                                src={items?.[activeStep]?.image.url}
                                alt={items?.[activeStep]?.image?.title}
                            />
                        </Box>
                        <Stepper
                            alternativeLabel
                            nonLinear
                            activeStep={activeStep}
                            className={classes.stepper}
                        >
                            {items?.map((item, index) => {
                                return item?.type?.includes("step") && (
                                    <Step
                                        className={classes.step}
                                        key={item?.sys?.id}
                                    >
                                        <StepButton
                                            onClick={() => handleChangeStep(index)}
                                        >
                                            {item?.title}
                                        </StepButton>
                                    </Step>
                                );
                            })}
                        </Stepper>
                        <Typography
                            variant="h4"
                            className={classes.title}
                        >
                            {items[activeStep]?.title}
                        </Typography>
                        <div className={classes.mb5}>
                            <TutorialElements
                                element={items[activeStep]}
                                elementProps={items[activeStep]?.type === "tutorial_step_text_price" ? { price: shareData?.averagePrice } : undefined}
                            />
                        </div>
                        <div className={classes.contentButton}>
                            {activeStep === items.length - 2 ? (
                                <CustomButton
                                    variant={"contained"}
                                    onClick={handleClose}
                                >
                                    {i18n("dialog.marketplace_onboarding.finish_label")}
                                </CustomButton>
                            ) : (
                                <CustomButton
                                    variant={"contained"}
                                    onClick={() => handleChangeStep(activeStep + 1)}
                                >
                                    {i18n("dialog.marketplace_onboarding.next_label")}
                                </CustomButton>
                            )
                            }
                        </div>
                        {items?.map((item) => item?.type?.includes("tutorial_footer") ? (
                            <TutorialFooter
                                key={item?.sys?.id}
                                componentId={item?.sys?.id}
                            />
                        ) : null)}
                    </div>
                }
            </Box>
        </NoIndexDialog>
    );
};

TutorialStepperDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onCloseTip: PropTypes.func,
};

export default TutorialStepperDialog;

